import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DentalBidirectionalUser, DentalBidirectionalRole, DCO } from '../../../app.service';

type RoleName = 'SystemAdmin' | 'ProviderUser' | 'Reviewer' | 'Admin' | 'SuperAdmin' | 'DCOUser';


const EXTERNAL_ALLOWED_ROLE_NAMES: RoleName[] = ['DCOUser', 'ProviderUser'];
const INTERNAL_DOMAIN = '@careoregon.org';

function isInternalEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.trim().toLowerCase().endsWith(INTERNAL_DOMAIN);
}

@Component({
  selector: 'app-user-edit-dialog',
  templateUrl: './user-edit-dialog.component.html',
  styleUrls: ['./user-edit-dialog.component.scss']
})
export class UserEditDialogComponent implements OnInit {
  form: FormGroup;
  availableRoles: DentalBidirectionalRole[] = [];
  availableDcos: DCO[] = [];
  user: DentalBidirectionalUser = null;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<UserEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { user?: DentalBidirectionalUser; roles: DentalBidirectionalRole[]; dcos: DCO[]; }
  ) {
    this.user = data.user;
    this.availableRoles = data.roles;
    this.availableDcos = data.dcos;
    console.log(this.availableDcos);
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  initializeForm(): void {
    const user = this.user;

    this.form = this.fb.group({
      firstName: [user?.firstName || '', Validators.required],
      lastName: [user?.lastName || '', Validators.required],
      emailAddress: [user?.emailAddress || '', [Validators.required, Validators.email]],
      roles: [[], Validators.required],
      dcoId: [user?.dentalCareOrganizationId ?? null],
      isActive: [user?.isActive ?? true, Validators.required],
    }, { validators: this.rolesGuardValidator() }); // <-- attach custom validator

    if (user?.roles?.length && this.availableRoles.length) {
      this.setUserRolesInForm();
    }

    // React to email changes: when email flips internal/external, adjust selected roles and UI
    this.form.get('emailAddress')!.valueChanges.subscribe(email => {
      const internal = isInternalEmail(email);

      // If external, strip any roles not in the allowed list
      if (!internal) {
        const rolesCtrl = this.form.get('roles')!;
        const current: DentalBidirectionalRole[] = rolesCtrl.value ?? [];
        const filtered = current.filter(r => EXTERNAL_ALLOWED_ROLE_NAMES.includes((r.name as RoleName)));
        if (filtered.length !== current.length) {
          rolesCtrl.setValue(filtered);
        }
      }
    });
  }

  get isExternalUser(): boolean {
    const email = this.form?.get('emailAddress')?.value as string;
    return !isInternalEmail(email);
  }

  private rolesGuardValidator() {
    return (group: FormGroup) => {
      const email = group.get('emailAddress')?.value as string;
      const roles = (group.get('roles')?.value as DentalBidirectionalRole[]) ?? [];

      // Valid by default
      let invalid = false;

      if (!isInternalEmail(email)) {
        // External: only DCOUser / ProviderUser allowed
        invalid = roles.some(r => !EXTERNAL_ALLOWED_ROLE_NAMES.includes((r.name as RoleName)));
      }

      // Set an error on the group so Save button disables and we can show a message
      if (invalid) {
        group.setErrors({ externalRoleViolation: true });
      } else {
        // Preserve any other errors
        const errors = { ...(group.errors ?? {}) };
        delete errors['externalRoleViolation'];
        group.setErrors(Object.keys(errors).length ? errors : null);
      }
      return group.errors;
    };
  }

  setUserRolesInForm(): void {
    const selectedRoles = this.availableRoles.filter(ar =>
      this.user?.roles?.some(ur => ur.id === ar.id)
    );
    this.form.get('roles')?.setValue(selectedRoles);
  }

  save(): void {
    if (this.form.invalid) return;

    const formValues = this.form.value;

    // Enforce again on save
    const email: string = formValues.emailAddress;
    let roles: DentalBidirectionalRole[] = formValues.roles ?? [];

    if (!isInternalEmail(email)) {
      roles = roles.filter(r => EXTERNAL_ALLOWED_ROLE_NAMES.includes((r.name as RoleName)));
    }

    const updatedUser: DentalBidirectionalUser = {
      id: this.data.user?.id ?? 0,
      userId: this.data.user?.userId ?? '',
      externalUserId: this.data.user?.externalUserId ?? '',
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      emailAddress: email,
      dentalCareOrganizationId: formValues.dcoId,
      isActive: formValues.isActive,
      roles,
      fullName: `${formValues.firstName} ${formValues.lastName}`
    };

    this.dialogRef.close(updatedUser);
  }

}
